// 用户登录页面 - 现代简约风格
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '../lib/api';
import { LogIn, Mail, Lock, Eye, EyeOff, RefreshCw } from 'lucide-react';
import Toast from '../components/Toast';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const [error, setError] = useState('');
  const [errorType, setErrorType] = useState<'client' | 'server' | ''>('');
  const [showEmailVerificationModal, setShowEmailVerificationModal] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  const navigate = useNavigate();

  const [successMessage, setSuccessMessage] = useState('');

  const [toastConfig, setToastConfig] = useState<{
  isVisible: boolean;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
}>({
  isVisible: false,
  type: 'success',
  title: '',
  message: ''
});

  // 处理GitHub登录回调
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const apiKey = urlParams.get('api_key');
    const email = urlParams.get('email');
    const errorParam = urlParams.get('error');

    if (success === 'true' && apiKey) {
      // GitHub登录成功
      localStorage.setItem('apiKey', apiKey);
      if (email) {
        localStorage.setItem('email', decodeURIComponent(email));
        localStorage.setItem('username', decodeURIComponent(email).split('@')[0]);
      }
      
      // 清除URL参数并跳转
      window.history.replaceState({}, document.title, '/login');
      navigate('/dashboard/models');
    } else if (errorParam) {
      // GitHub登录失败，显示错误信息
      const errorMessages: { [key: string]: string } = {
        'access_denied': 'GitHub login was cancelled.',
        'token_error': 'Failed to get GitHub access token.',
        'no_email': 'GitHub account must have a public email address.',
        'create_user_failed': 'Failed to create user account.',
        'create_key_failed': 'Failed to create API key.',
        'oauth_failed': 'GitHub login failed. Please try again.'
      };
      
      setError(errorMessages[errorParam] || 'GitHub login failed. Please try again.');
      setErrorType('server');
      
      // 清除URL参数
      window.history.replaceState({}, document.title, '/login');
    }
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  setError('');
  
  // 自定义验证
  if (!email.trim()) {
    setError('Please enter your email address');
    setErrorType('client');
    return;
  }
  if (!email.includes('@') || !email.includes('.')) {
    setError('Please enter a valid email address');
    setErrorType('client');
    return;
  }
  if (!password.trim()) {
    setError('Please enter your password');
    setErrorType('client');
    return;
  }
  
  setLoading(true);
  
  try {
    const response = await api.post('/login', {
      email,
      password,
    });

    const { api_key, user_id } = response.data;
localStorage.setItem('apiKey', api_key);

// 立即获取完整用户信息并保存
try {
  const profileResponse = await api.get('/api/user/profile', {
    headers: {
      'x-api-key': api_key
    }
  });
  
  if (profileResponse.data) {
    localStorage.setItem('email', profileResponse.data.email || email);
    localStorage.setItem('username', profileResponse.data.username || 'User');
  }
} catch (err) {
  console.error('Failed to fetch user profile after login:', err);
  // 保存基本信息作为后备
  localStorage.setItem('email', email);
  localStorage.setItem('username', email.split('@')[0]);
}

navigate('/dashboard/models');
  } catch (err: any) {
    let errorMessage = 'Something went wrong. Please try again.';

    if (err.response) {
      const status = err.response.status;
      const serverMessage = err.response?.data?.error?.message || err.response?.data?.message;

      switch (status) {
        case 400:
          errorMessage = serverMessage || 'Invalid email or password format.';
          break;
        case 401:
          errorMessage = 'Invalid email or password. Please check your credentials.';
          break;
        case 403:
          errorMessage = 'Account access is forbidden. Please contact support.';
          break;
        case 404:
          errorMessage = 'Account not found. Please check your email or sign up.';
          break;
        case 422:
          if (serverMessage?.toLowerCase().includes('email not verified') || serverMessage?.toLowerCase().includes('unverified')) {
            setUnverifiedEmail(email);
            setShowEmailVerificationModal(true);
            return; // 不设置错误信息，直接显示弹窗
          } else {
            errorMessage = serverMessage || 'Please verify your email address before signing in.';
          }
          break;
        case 429:
          errorMessage = 'Too many login attempts. Please try again later.';
          break;
        case 500:
          errorMessage = 'Server error. Please try again later.';
          break;
        case 503:
          errorMessage = 'Service temporarily unavailable. Please try again later.';
          break;
        default:
          errorMessage = serverMessage || `Login failed (Error ${status}). Please try again.`;
      }
    } else if (err.request) {
      errorMessage = 'Network error. Please check your internet connection.';
    }

    setError(errorMessage);
    setErrorType('server');
  } finally {
    setLoading(false);
  }
};

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 flex items-center justify-center px-4 relative overflow-hidden">
      {/* 网格背景 */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 opacity-15" style={{
          backgroundImage: `
            linear-gradient(rgba(255, 255, 255, 0.2) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.2) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 w-full max-w-sm border border-white/20 shadow-2xl animate-in slide-in-from-bottom-4 duration-700">
        {/* Logo和标题 */}
        <div className="text-center mb-6">
          <div className="relative mb-3">
            <h1 className="text-xl font-bold text-white text-center animate-in fade-in duration-1000 delay-300">ModelPool</h1>
            <img src={`${process.env.PUBLIC_URL}/mplogo.png`} alt="ModelPool" className="absolute right-full top-1/2 -translate-y-1/2 mr-2 h-8 w-8 animate-in spin-in duration-1000 delay-500" style={{left: 'calc(50% - 80px)'}} />
          </div>
          <p className="text-white/80 text-xs animate-in fade-in duration-1000 delay-700">Dive into the infinite possibilities of AI models</p>
        </div>

        {/* 登录表单 */}
        <form onSubmit={handleSubmit} className="animate-in fade-in duration-1000 delay-900" noValidate>
          {error && (
            <div className="bg-red-500/10 border border-red-400/30 text-red-300 px-4 py-3 rounded-lg text-sm backdrop-blur-sm flex items-center space-x-2 animate-in slide-in-from-top-2 duration-500 mb-4">
              <svg className="w-4 h-4 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          {/* 邮箱输入 */}
          <div className="space-y-1 animate-in slide-in-from-left-4 duration-600 delay-1000 mb-4">
            <label className="block text-white/90 text-xs font-medium">Email Address</label>
            <div className="relative">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (error && errorType === 'client' && e.target.value) {
                    setError('');
                    setErrorType('');
                  }
                }}
                className="w-full bg-white/10 border border-white/20 text-white placeholder-white/50 px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent backdrop-blur-sm text-sm transition-all duration-300 hover:border-white/40"
                placeholder="Enter your email"
                title="Please enter a valid email address"
                pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
              />
            </div>
          </div>

          {/* 密码输入 */}
          <div className="space-y-1 animate-in slide-in-from-right-4 duration-600 delay-1100 mb-4">
  <label className="block text-white/90 text-xs font-medium">Password</label>
  <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                required
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  // 只有在客户端验证错误时才清除错误
                  if (error && errorType === 'client') {
                    setError('');
                    setErrorType('');
                  }
                }}
                className="w-full bg-white/10 border border-white/20 text-white placeholder-white/50 px-3 py-2 pr-10 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent backdrop-blur-sm text-sm transition-all duration-300 hover:border-white/40"
                placeholder="Enter your password"
                title="Please enter your password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/60 hover:text-white/80 transition duration-200 hover:scale-110"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {/* 登录按钮 */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white font-medium py-2 px-3 rounded-lg hover:bg-blue-700 transition-all duration-300 flex items-center justify-center space-x-2 disabled:opacity-50 text-sm animate-in slide-in-from-bottom-4 duration-600 delay-1300 hover:scale-105 hover:shadow-lg mt-12"
          >
            {loading ? (
              <div className="flex items-center space-x-2">
                <div className="spinner"></div>
                Signing in...
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <LogIn className="h-4 w-4" />
                Sign In
              </div>
            )}
          </button>

          {/* GitHub登录按钮 - 暂时隐藏 */}
          {false && (
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/20"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-transparent text-white/70">Or continue with</span>
              </div>
            </div>
            
            <div className="mt-6">
              <button
                type="button"
                onClick={() => window.location.href = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8088'}/auth/github`}
                className="w-full flex justify-center items-center px-4 py-2 border border-white/20 rounded-lg bg-white/10 backdrop-blur-sm text-white font-medium hover:bg-white/20 transition-all duration-300 hover:border-white/40"
              >
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 0C4.477 0 0 4.484 0 10.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0110 4.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.203 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.942.359.31.678.921.678 1.856 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0020 10.017C20 4.484 15.522 0 10 0z" clipRule="evenodd"></path>
                </svg>
                Continue with GitHub
              </button>
            </div>
          </div>
          )}

          {/* 忘记密码和注册链接 */}
          <div className="text-center mt-6 space-y-3">
            <div>
              <Link to="/forgot-password" className="text-blue-400 hover:text-blue-300 text-sm font-medium transition duration-200">
                Forgot your password?
              </Link>
            </div>
            <div>
              <span className="text-white/70 text-sm">Don't have an account? </span>
              <Link to="/register" className="text-blue-400 hover:text-blue-300 text-sm font-medium transition duration-200">
                Create Account
              </Link>
            </div>
          </div>
        </form>
      </div>
      
      {/* 邮箱验证提示弹窗 */}
      {showEmailVerificationModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 animate-in fade-in duration-300">
          <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 w-full max-w-md mx-4 border border-white/20 shadow-2xl animate-in zoom-in-95 duration-500">
            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4 animate-in zoom-in duration-700 delay-200">
                <Mail className="w-8 h-8 text-yellow-400 animate-in fade-in duration-500 delay-500" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2 animate-in slide-in-from-bottom-2 duration-500 delay-300">Email Verification Required</h3>
              <p className="text-white/80 mb-6 text-sm animate-in fade-in duration-500 delay-400">
                Your email address <span className="font-medium text-yellow-300">{unverifiedEmail}</span> has not been verified yet.
                Please check your inbox and click the verification link to activate your account.
              </p>
              {successMessage && (
  <div className="bg-green-500/20 border border-green-400/30 text-green-300 px-4 py-3 rounded-lg text-sm backdrop-blur-sm flex items-center space-x-2 animate-in slide-in-from-top-2 duration-500 mb-4">
    <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
    </svg>
    <span>{successMessage}</span>
  </div>
)}
              <div className="space-y-3">
                <button
  onClick={async () => {
    try {
      setLoading(true);
      await api.post('/resend-verification', { email: unverifiedEmail });
      
      // 成功：关闭弹窗并显示绿色Toast
      setShowEmailVerificationModal(false);
      setUnverifiedEmail('');
      setSuccessMessage('');
      setToastConfig({
        isVisible: true,
        type: 'success',
        title: 'Verification Email Sent!',
        message: 'Please check your inbox and click the verification link.'
      });
      
    } catch (err: any) {
      console.error('Failed to resend verification email:', err);
      const errorMessage = err.response?.data?.error?.message || 'Failed to resend verification email. Please try again.';
      
      // 错误：保持弹窗打开，显示红色Toast
      setToastConfig({
        isVisible: true,
        type: 'error',
        title: 'Send Failed',
        message: errorMessage
      });
    } finally {
      setLoading(false);
    }
  }}
                  className="w-full bg-yellow-600 text-white font-medium py-2.5 px-4 rounded-lg hover:bg-yellow-700 transition-all duration-300 flex items-center justify-center space-x-2 animate-in slide-in-from-bottom-4 duration-500 delay-500 hover:scale-105"
                >
                  <RefreshCw className="w-4 h-4" />
                  <span>Resend Verification Email</span>
                </button>
                <button
  onClick={() => {
    setShowEmailVerificationModal(false);
    setUnverifiedEmail('');
    setSuccessMessage('');
  }}
                  className="w-full bg-white/10 text-white font-medium py-2.5 px-4 rounded-lg hover:bg-white/20 transition-all duration-300 animate-in slide-in-from-bottom-4 duration-500 delay-600 hover:scale-105"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Toast 通知组件 */}
      <Toast
        type={toastConfig.type}
        title={toastConfig.title}
        message={toastConfig.message}
        isVisible={toastConfig.isVisible}
        onClose={() => setToastConfig(prev => ({ ...prev, isVisible: false }))}
        duration={4000}
      />
    </div>
  );
};

export default Login;